import React, { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { bool, func, number, object } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import { Icons } from '../../../components';

import useDialogFocus from '../useDialogFocus';

import css from './FloatingFilterBar.module.css';

/**
 * Phone-only floating Filters/Sort control (measured from the prototype):
 * ONE segmented pill — [funnel Filters (n) | arrows Sort] — centered above
 * the bottom nav. Filters opens the bottom-sheet FilterPanel; Sort opens its
 * own small bottom sheet ("Sort by" + full-label rows, selected = quiet tint
 * + check). Hidden from --viewportSmall up and while either sheet is open.
 */
const SortSheet = props => {
  const { sortConfig, urlQueryParams, hasKeywords, onSortBy, onClose, intl } = props;
  const sheetRef = useRef(null);
  useDialogFocus(sheetRef, true);
  const relevanceKey = sortConfig.relevanceKey;
  const queryParamName = sortConfig.queryParamName || 'sort';
  const options = (sortConfig.options || []).filter(o => hasKeywords || o.key !== relevanceKey);
  const defaultKey = hasKeywords && relevanceKey ? relevanceKey : options[0]?.key;
  const currentValue = urlQueryParams[queryParamName] || defaultKey;
  const labelOf = option =>
    option.labelTranslationKey
      ? intl.formatMessage({ id: option.labelTranslationKey })
      : option.label || option.key;

  if (typeof document === 'undefined') return null;
  return ReactDOM.createPortal(
    <>
      <div className={css.sheetOverlay} onClick={onClose} />
      <div
        className={css.sortSheet}
        role="dialog"
        aria-modal="true"
        aria-labelledby="FloatingFilterBar.sortBy"
        tabIndex={-1}
        ref={sheetRef}
        onKeyDown={e => {
          if (e.key === 'Escape') onClose();
        }}
      >
        <div className={css.sheetHandle} />
        <div className={css.sortSheetHead} id="FloatingFilterBar.sortBy">
          <FormattedMessage id="FloatingFilterBar.sortBy" />
        </div>
        {options.map(option => {
          const isSelected = option.key === currentValue;
          return (
            <button
              type="button"
              key={option.key}
              className={classNames(css.sortRow, { [css.sortRowSelected]: isSelected })}
              onClick={() => {
                onSortBy(queryParamName, option.key === relevanceKey ? null : option.key);
                onClose();
              }}
            >
              <span className={css.sortCheck}>{isSelected ? '✓' : ''}</span>
              {labelOf(option)}
            </button>
          );
        })}
      </div>
    </>,
    document.body
  );
};

const FloatingFilterBarComponent = props => {
  const {
    onOpenPanel,
    activeCount,
    sortConfig,
    urlQueryParams,
    hasKeywords,
    onSortBy,
    isPanelOpen,
    intl,
  } = props;
  const [isSortOpen, setIsSortOpen] = useState(false);
  const showSort = !!(sortConfig?.active && onSortBy);

  // Prototype: the floating pill hides while a sheet is up
  if (isPanelOpen) return null;

  return (
    <>
      {isSortOpen ? null : (
        <div className={css.root}>
          <button type="button" className={css.filterButton} onClick={onOpenPanel}>
            <Icons name="filter" />
            {activeCount > 0 ? (
              <FormattedMessage id="FloatingFilterBar.filtersWithCount" values={{ count: activeCount }} />
            ) : (
              <FormattedMessage id="FloatingFilterBar.filters" />
            )}
          </button>
          {showSort ? (
            <button type="button" className={css.sortButton} onClick={() => setIsSortOpen(true)}>
              <Icons name="sortArrows" />
              <FormattedMessage id="FloatingFilterBar.sort" />
            </button>
          ) : null}
        </div>
      )}
      {isSortOpen && showSort ? (
        <SortSheet
          sortConfig={sortConfig}
          urlQueryParams={urlQueryParams}
          hasKeywords={hasKeywords}
          onSortBy={onSortBy}
          onClose={() => setIsSortOpen(false)}
          intl={intl}
        />
      ) : null}
    </>
  );
};

FloatingFilterBarComponent.defaultProps = {
  activeCount: 0,
  sortConfig: null,
  urlQueryParams: {},
  hasKeywords: false,
  onSortBy: null,
  isPanelOpen: false,
};

FloatingFilterBarComponent.propTypes = {
  onOpenPanel: func.isRequired,
  activeCount: number,
  sortConfig: object,
  urlQueryParams: object,
  hasKeywords: bool,
  onSortBy: func,
  isPanelOpen: bool,
  intl: intlShape.isRequired,
};

export default injectIntl(FloatingFilterBarComponent);
